import { MEASURED_MODERATION_USAGE } from "./MeasuredModerationUsage";
import { ENABLE_CLEF_FLASH_IMAGES } from "./SharedConstants";
/**
 * What each moderation provider charges, and how a call's usage turns into cost. The single source
 * for moderation prices: production providers (shared-backend ModerationProviders.ts) attach
 * `estimatedCostUsd` to every result from it, the back office Pricing tab models its provider chains
 * with it (ProviderPricing.ts), and the benchmark reports run costs with it.
 */

export interface ProviderPrice {
  provider: string;
  product: string;
  unit: string;
  usdPerUnit: number;
  source: string;
  checkedAt: string;
  note?: string;
}

const MODERATION_SNAPSHOT_DATE = "2026-09-02";

export const MODERATION_PRICES = {
  jevInputTokens: {
    provider: "TypeSafe",
    product: "Jev input tokens (images billed as input tokens; output free)",
    unit: "input token",
    usdPerUnit: 0.042 / 1_000_000,
    source: "https://docs.typesafe.ai/models",
    checkedAt: "2026-10-15",
  },
  liquidD1UltraInputTokens: {
    provider: "Liquid AI",
    product: "d1-ultra input tokens (output free)",
    unit: "input token",
    usdPerUnit: 0.042 / 1_000_000,
    source: "https://www.liquid.ai/blog/d1-ultra",
    checkedAt: "2026-10-28",
  },
  d1UltraInputTokens: {
    provider: "OpenRouter",
    product: "Liquid d1-ultra input tokens (output free)",
    unit: "input token",
    usdPerUnit: 0.042 / 1_000_000,
    source: "https://openrouter.ai/liquid/d1-ultra",
    checkedAt: "2026-10-26",
  },
  clefFlashInputTokens: {
    provider: "Cloudflare",
    product: "Workers AI Clef Flash input tokens",
    unit: "input token",
    usdPerUnit: 0.038 / 1_000_000,
    source: "https://developers.cloudflare.com/workers-ai/models/clef-flash/",
    checkedAt: "2026-10-21",
  },
  clefInputTokens: {
    provider: "Cloudflare",
    product: "Workers AI Clef input tokens",
    unit: "input token",
    usdPerUnit: 0.24 / 1_000_000,
    source: "https://developers.cloudflare.com/workers-ai/models/clef/",
    checkedAt: "2026-10-10",
  },
  clefOmniInputTokens: {
    provider: "Cloudflare",
    product: "Workers AI Clef Omni input tokens",
    unit: "input token",
    usdPerUnit: 0.15 / 1_000_000,
    source: "https://developers.cloudflare.com/workers-ai/models/clef-omni/",
    checkedAt: "2026-10-10",
  },
  openAiModeration: {
    provider: "OpenAI",
    product: "omni-moderation-latest",
    unit: "request",
    usdPerUnit: 0,
    source: "https://developers.openai.com/api/docs/models/omni-moderation-latest",
    checkedAt: MODERATION_SNAPSHOT_DATE,
    note: "Published as free.",
  },
  mistralModeration: {
    provider: "Mistral",
    product: "mistral-moderation-latest",
    unit: "request",
    usdPerUnit: 0,
    source: "https://mistral.ai/pricing/api/",
    checkedAt: MODERATION_SNAPSHOT_DATE,
    note: "Published as free.",
  },
  azureContentSafetyText: {
    provider: "Azure",
    product: "Content Safety text (Standard)",
    unit: "text record (up to 1,000 characters)",
    usdPerUnit: 0.375 / 1_000,
    source: "https://azure.microsoft.com/en-us/pricing/details/content-safety/",
    checkedAt: MODERATION_SNAPSHOT_DATE,
  },
  azureContentSafetyImage: {
    provider: "Azure",
    product: "Content Safety image (Standard)",
    unit: "image",
    usdPerUnit: 0.75 / 1_000,
    source: "https://azure.microsoft.com/en-us/pricing/details/content-safety/",
    checkedAt: MODERATION_SNAPSHOT_DATE,
  },
  googleVisionSafeSearch: {
    provider: "Google Cloud",
    product: "Cloud Vision SafeSearch (1,001–5M units/month)",
    unit: "image",
    usdPerUnit: 1.5 / 1_000,
    source: "https://cloud.google.com/vision/pricing",
    checkedAt: "2026-10-15",
    note: "First 1,000 units/month free; free when sent with Label Detection.",
  },
  sightengineOperation: {
    provider: "Sightengine",
    product: "Operation (Starter/Pro overage)",
    unit: "operation",
    usdPerUnit: 0.002,
    source: "https://sightengine.com/pricing",
    checkedAt: "2026-10-15",
    note: "Plans include 10,000–40,000 operations a month; this is the overage rate.",
  },
  hiveVisualModeration: {
    provider: "Hive",
    product: "Visual moderation (self-serve tier)",
    unit: "request",
    usdPerUnit: 3 / 1_000,
    source: "https://thehive.ai/pricing",
    checkedAt: "2026-10-15",
    note: "Self-serve is limited to 100 requests a day; production needs an annual contract.",
  },
  hiveTextModeration: {
    provider: "Hive",
    product: "Text moderation (self-serve tier)",
    unit: "request (up to 1,024 characters)",
    usdPerUnit: 0.5 / 1_000,
    source: "https://thehive.ai/pricing",
    checkedAt: "2026-10-15",
    note: "Self-serve is limited to 100 requests a day; production needs an annual contract.",
  },
  awsRekognitionModeration: {
    provider: "AWS",
    product: "Rekognition DetectModerationLabels (Group 2, first 1M images/month)",
    unit: "image",
    usdPerUnit: 0.001,
    source: "https://aws.amazon.com/rekognition/pricing/",
    checkedAt: MODERATION_SNAPSHOT_DATE,
  },
} as const satisfies Record<string, ProviderPrice>;

export type ModerationPriceId = keyof typeof MODERATION_PRICES;

export const MODERATION_PROVIDER_IDS = [
  "liquid",
  "d1-ultra",
  "clef-flash",
  "clef",
  "clef-omni",
  "jev",
  "openai",
  "mistral",
  "azure",
  "aws",
  "google-safesearch",
  "sightengine",
  "hive",
] as const;
export type ModerationProviderId = (typeof MODERATION_PROVIDER_IDS)[number];

/**
 * Every provider that can moderate each input type, in fallback order. The active chains below are
 * what production runs: createImageModerationProviders and createTextModerationProviders build their
 * providers in that order, and the back office models costs over them.
 */
export const IMAGE_MODERATION_CHAIN = ["jev", "clef-flash", "openai", "azure", "aws"] as const;
export const TEXT_MODERATION_CHAIN = [
  "liquid",
  "d1-ultra",
  "jev",
  "openai",
  "mistral",
  "azure",
] as const;
export type ImageModerationProviderId = (typeof IMAGE_MODERATION_CHAIN)[number];
export type TextModerationProviderId = (typeof TEXT_MODERATION_CHAIN)[number];
export const ACTIVE_IMAGE_MODERATION_CHAIN: readonly ImageModerationProviderId[] =
  IMAGE_MODERATION_CHAIN.filter(
    (provider) => ENABLE_CLEF_FLASH_IMAGES || provider !== "clef-flash",
  );
export const ACTIVE_TEXT_MODERATION_CHAIN: readonly TextModerationProviderId[] =
  TEXT_MODERATION_CHAIN;
/** The chain positions a pricing share applies to; later fallbacks are assumed unused. */
export const CHAIN_POSITIONS = ["primary", "firstFallback", "secondFallback"] as const;
export type ChainPosition = (typeof CHAIN_POSITIONS)[number];

/** One moderation call: what was moderated and the usage the provider reported, if any. */
export interface ModerationUsage {
  inputType: "text" | "image";
  /** Characters of moderated text. */
  characters?: number;
  /** Input tokens the provider reported for the whole call (all question batches). */
  inputTokens?: number;
}

export interface ModerationCostLine {
  label: string;
  priceId: ModerationPriceId;
  quantity: number;
  usd: number;
}

const line = (label: string, priceId: ModerationPriceId, quantity: number): ModerationCostLine => ({
  label,
  priceId,
  quantity,
  usd: MODERATION_PRICES[priceId].usdPerUnit * quantity,
});

/**
 * Input tokens for one call: what the provider reported, or the benchmark's measurement for the
 * provider and input type (MeasuredModerationUsage), scaled by length for text.
 */
const decisionTokens = (
  provider: "d1-ultra" | "jev" | "clef-flash" | "clef" | "clef-omni",
  usage: ModerationUsage,
) => {
  if (usage.inputTokens !== undefined) return usage.inputTokens;
  if (provider === "clef-omni") return 0;
  const measured = MEASURED_MODERATION_USAGE[provider][usage.inputType];
  if (!measured)
    throw new Error(
      `No measured ${usage.inputType} usage for ${provider}; record it with the benchmark.`,
    );
  return Math.round(
    measured.inputTokensPerCall + (measured.inputTokensPerCharacter ?? 0) * (usage.characters ?? 0),
  );
};

/** How one call to a provider is priced: its name and the cost lines for the call's usage. */
export interface ModerationPricing {
  name: string;
  lines(usage: ModerationUsage): ModerationCostLine[];
}

/** Cost lines for one call to a moderation provider. */
export const MODERATION_PROVIDER_PRICING: Record<ModerationProviderId, ModerationPricing> = {
  liquid: {
    name: "Liquid d1-ultra (direct)",
    // Forecasts reuse the same model's OpenRouter measurement; actual calls use reported tokens.
    lines: (usage) => [
      line("Input tokens", "liquidD1UltraInputTokens", decisionTokens("d1-ultra", usage)),
    ],
  },
  "d1-ultra": {
    name: "Liquid d1-ultra (OpenRouter)",
    lines: (usage) => [
      line("Input tokens", "d1UltraInputTokens", decisionTokens("d1-ultra", usage)),
    ],
  },
  jev: {
    name: "TypeSafe Jev",
    lines: (usage) => [line("Input tokens", "jevInputTokens", decisionTokens("jev", usage))],
  },
  "clef-flash": {
    name: "Cloudflare Clef Flash",
    lines: (usage) => [
      line("Input tokens", "clefFlashInputTokens", decisionTokens("clef-flash", usage)),
    ],
  },
  clef: {
    name: "Cloudflare Clef",
    lines: (usage) => [line("Input tokens", "clefInputTokens", decisionTokens("clef", usage))],
  },
  "clef-omni": {
    name: "Cloudflare Clef Omni",
    lines: (usage) => [
      line("Input tokens", "clefOmniInputTokens", decisionTokens("clef-omni", usage)),
    ],
  },
  openai: {
    name: "OpenAI omni-moderation-latest",
    lines: () => [line("Moderation request", "openAiModeration", 1)],
  },
  mistral: {
    name: "Mistral mistral-moderation-latest",
    lines: () => [line("Moderation request", "mistralModeration", 1)],
  },
  azure: {
    name: "Azure Content Safety",
    lines: (usage) =>
      usage.inputType === "image"
        ? [line("Image analysis", "azureContentSafetyImage", 1)]
        : [
            line(
              "Text records",
              "azureContentSafetyText",
              Math.max(1, Math.ceil((usage.characters ?? 0) / 1_000)),
            ),
          ],
  },
  sightengine: {
    name: "Sightengine",
    lines: (usage) =>
      usage.inputType === "image"
        ? // nudity-2.1, the violence group, drugs and offensive-2.0 (SIGHTENGINE_IMAGE_MODELS).
          [line("Image operations", "sightengineOperation", 4)]
        : // The general and self-harm text models, each per 1,000 characters.
          [
            line(
              "Text operations",
              "sightengineOperation",
              2 * Math.max(1, Math.ceil((usage.characters ?? 0) / 1_000)),
            ),
          ],
  },
  hive: {
    name: "Hive",
    lines: (usage) =>
      usage.inputType === "image"
        ? [line("Visual moderation request", "hiveVisualModeration", 1)]
        : [
            line(
              "Text moderation requests",
              "hiveTextModeration",
              Math.max(1, Math.ceil((usage.characters ?? 0) / 1_024)),
            ),
          ],
  },
  "google-safesearch": {
    name: "Google Cloud Vision SafeSearch",
    lines: () => [line("SafeSearch image", "googleVisionSafeSearch", 1)],
  },
  aws: {
    name: "AWS Rekognition DetectModerationLabels",
    lines: () => [line("DetectModerationLabels image", "awsRekognitionModeration", 1)],
  },
};

export const isModerationProviderId = (provider: string): provider is ModerationProviderId =>
  (MODERATION_PROVIDER_IDS as readonly string[]).includes(provider);

/**
 * CalmLens's own routing, priced as a call to each input type's primary provider. A fallback's
 * actual cost is on the result it answered.
 */
export const CALMLENS_PRICING: ModerationPricing = {
  name: "CalmLens (production routing)",
  lines: (usage) =>
    MODERATION_PROVIDER_PRICING[
      usage.inputType === "image"
        ? ACTIVE_IMAGE_MODERATION_CHAIN[0]!
        : ACTIVE_TEXT_MODERATION_CHAIN[0]
    ].lines(usage),
};

/** A provider's pricing by name, or undefined for a provider with no published rate here. */
export const moderationPricing = (provider: string): ModerationPricing | undefined =>
  provider === "calmlens"
    ? CALMLENS_PRICING
    : isModerationProviderId(provider)
      ? MODERATION_PROVIDER_PRICING[provider]
      : undefined;

/** Estimated USD cost of one moderation call. */
export const estimateModerationCostUsd = (
  provider: ModerationProviderId,
  usage: ModerationUsage,
): number =>
  MODERATION_PROVIDER_PRICING[provider]
    .lines(usage)
    .reduce((total, costLine) => total + costLine.usd, 0);
